/**
 * Per-tenant token store.
 *
 * Multi-tenant SaaS: the SAME customer may sign in to several tenants in
 * parallel tabs. The naive `localStorage.kb_token` slot loses Tenant A's
 * session the moment Tenant B logs in. We solve that by keying every
 * token slot by the active tenant slug:
 *
 *   kb_token:default            → default tenant
 *   kb_token:boutique-mango     → Boutique Mango tenant
 *   kb_token:__super__          → Super-admin (global, tenant-agnostic)
 *
 * On every request, the axios interceptor:
 *   1. resolves the tenant slug from URL / host / explicit override
 *   2. picks the matching token slot
 *   3. attaches it to Authorization
 *
 * Super-admin is special — their JWT carries `tenant_id: null` and the
 * backend treats them as platform-global. We store it under the reserved
 * slug `__super__` so it doesn't get clobbered by tenant logins.
 *
 * Cross-tab sync: any login/logout fires a BroadcastChannel message so
 * sister tabs on the same tenant update instantly.
 *
 * Back-compat: if a legacy `kb_token` slot exists at module init, we
 * migrate it into the current tenant's slot (best-effort, one-shot).
 */

import { resolveTenantSlug } from "./tenant";

const TOKEN_PREFIX = "kb_token:";
const USER_PREFIX = "kb_user:";
const SUPER_SLUG = "__super__";
const LEGACY_TOKEN_KEY = "kb_token";
const LEGACY_USER_KEY = "kb_user";

const SYNC_CHANNEL = "kb-auth-sync";
let channel = null;
try {
  if (typeof BroadcastChannel !== "undefined") {
    channel = new BroadcastChannel(SYNC_CHANNEL);
  }
} catch {
  channel = null;
}

function safeGet(key) {
  try { return localStorage.getItem(key); }
  catch { return null; }
}
function safeSet(key, value) {
  try { localStorage.setItem(key, value); return true; }
  catch { return false; }
}
function safeRemove(key) {
  try { localStorage.removeItem(key); }
  catch { /* sandboxed */ }
}

/**
 * Decide which storage slot to use for the *current page context*.
 * - Super admin pages live at `/superadmin/*` regardless of host, so they
 *   always read/write the `__super__` slot.
 * - Otherwise we key by the resolved tenant slug.
 */
export function currentTokenSlug() {
  if (typeof window !== "undefined") {
    const p = window.location.pathname || "";
    // Super-admin pages live under `/superadmin/*` — but the URL may also
    // be tenant-prefixed (`/t/{slug}/superadmin/*`) when the user
    // deep-links from within the storefront. Either form should resolve
    // to the `__super__` slot so axios attaches the right token.
    if (p.startsWith("/superadmin/") || p === "/superadmin"
        || p.includes("/superadmin/") || p.endsWith("/superadmin")) {
      return SUPER_SLUG;
    }
  }
  return resolveTenantSlug() || "default";
}

function tokenKey(slug) {
  return `${TOKEN_PREFIX}${slug}`;
}
function userKey(slug) {
  return `${USER_PREFIX}${slug}`;
}

// ---------------------------------------------------------------------------
// One-shot legacy migration. Older builds stored a single global token. If we
// find one on first load, move it into the current slot so the user doesn't
// get logged out by the upgrade. Runs once per page load.
// ---------------------------------------------------------------------------
function migrateLegacyIfNeeded() {
  const legacyToken = safeGet(LEGACY_TOKEN_KEY);
  if (!legacyToken) return;

  const slug = currentTokenSlug();
  // Only migrate if THIS tenant doesn't already have a token in the new
  // slot (preserve whatever the user has in the new format).
  if (!safeGet(tokenKey(slug))) {
    safeSet(tokenKey(slug), legacyToken);
    const legacyUser = safeGet(LEGACY_USER_KEY);
    if (legacyUser) safeSet(userKey(slug), legacyUser);
  }
  // Always purge the legacy slot once migrated — it's intentionally
  // mutually exclusive with the new keys so we don't accidentally
  // double-track sessions.
  safeRemove(LEGACY_TOKEN_KEY);
  safeRemove(LEGACY_USER_KEY);
}
migrateLegacyIfNeeded();

// ---------------------------------------------------------------------------
// Public API — tenant-scoped read/write
// ---------------------------------------------------------------------------

/** Read the token for the active tenant (or super-admin slot on /superadmin). */
export function getToken() {
  return safeGet(tokenKey(currentTokenSlug()));
}

/** Read the cached user record for the active tenant slot. */
export function getCachedUser() {
  const raw = safeGet(userKey(currentTokenSlug()));
  if (!raw) return null;
  try { return JSON.parse(raw); }
  catch { return null; }
}

/**
 * Persist a token (and optional cached user) for the active tenant slot.
 * If the JWT belongs to a super_admin user (role === "super_admin"),
 * we ALWAYS write to the __super__ slot — they're not bound to a tenant.
 */
export function setToken(token, user = null) {
  if (!token) return;
  const slug = user?.role === "super_admin"
    ? SUPER_SLUG
    : currentTokenSlug();
  safeSet(tokenKey(slug), token);
  if (user) safeSet(userKey(slug), JSON.stringify(user));
  channel?.postMessage({ type: "login", slug });
}

/** Clear the active tenant's session. */
export function clearToken({ broadcast = true } = {}) {
  const slug = currentTokenSlug();
  safeRemove(tokenKey(slug));
  safeRemove(userKey(slug));
  if (broadcast) channel?.postMessage({ type: "logout", slug });
}

/** Clear EVERY session this browser knows about — "sign out everywhere". */
export function clearAllTokens() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith(TOKEN_PREFIX) || k.startsWith(USER_PREFIX))) {
        keys.push(k);
      }
    }
    keys.forEach(safeRemove);
  } catch { /* sandboxed */ }
  channel?.postMessage({ type: "logout-all" });
}

/**
 * Subscribe to cross-tab auth changes for the active tenant slot.
 * Returns an unsubscribe function. Used by AuthContext to refresh state
 * the moment a sister tab on the same tenant logs in / out.
 */
export function subscribeAuthSync(handler) {
  if (!channel) return () => {};
  const ours = currentTokenSlug();
  const listener = (event) => {
    const { type, slug } = event.data || {};
    if (type === "logout-all" || slug === ours) handler({ type, slug });
  };
  channel.addEventListener("message", listener);
  return () => channel.removeEventListener("message", listener);
}

export const __TEST__ = { SUPER_SLUG, TOKEN_PREFIX, USER_PREFIX };
